import { Component, OnInit } from '@angular/core';
import { ApiService } from '../core/api.service';
import { AuthService } from '../core/auth.service';
import { ToastService } from '../core/toast.service';

@Component({
  selector: 'app-fundamental-settings',
  template: `
  <h1 class="page-title">AI Natural Learning</h1>
  <p class="muted">Structural setup — creating a new tenant, its branches, and custom roles with module-level access.</p>

  <div class="tabs">
    <a [class.active]="tab === 'org'" (click)="tab = 'org'">Organizations</a>
    <a [class.active]="tab === 'branch'" (click)="tab = 'branch'; loadBranches()">Branches</a>
    <a [class.active]="tab === 'role'" (click)="tab = 'role'; loadRoles()">Roles &amp; Permissions</a>
  </div>

  <div class="org-pick" *ngIf="auth.isSuperAdmin && tab !== 'org'">
    <label>Organization</label>
    <select [(ngModel)]="orgId" (ngModelChange)="tab === 'branch' ? loadBranches() : loadRoles()">
      <option *ngFor="let o of orgs" [ngValue]="o.organizationId">{{ o.orgName }} ({{ o.orgCode }})</option>
    </select>
  </div>

  <!-- ============ ORGANIZATIONS ============ -->
  <div class="split" *ngIf="tab === 'org'">
    <div class="card side">
      <div class="card-head">{{ orgEdit.organizationId ? 'Edit Organization' : 'New Organization' }}</div>
      <ng-container *ngIf="auth.isSuperAdmin || orgEdit.organizationId; else noCreate">
        <label>Code</label><input [(ngModel)]="orgEdit.orgCode" [disabled]="!!orgEdit.organizationId" placeholder="e.g. AAA-ACADEMY" maxlength="20">
        <label>Name</label><input [(ngModel)]="orgEdit.orgName" placeholder="Organization name">
        <label>Email</label><input [(ngModel)]="orgEdit.email" type="email">
        <label>Phone</label><input [(ngModel)]="orgEdit.phone">
        <label>Address</label><textarea rows="2" [(ngModel)]="orgEdit.address"></textarea>
        <label class="check" *ngIf="orgEdit.organizationId && auth.isSuperAdmin && !orgEdit.isDefault">
          <input type="checkbox" [(ngModel)]="orgEdit.isActive"> Active
        </label>
        <div class="btn-row">
          <button class="btn primary" (click)="saveOrg()">{{ orgEdit.organizationId ? 'Save' : 'Create' }}</button>
          <button class="btn ghost" *ngIf="orgEdit.organizationId" (click)="orgEdit = newOrg()">Cancel</button>
        </div>
        <p class="muted small" *ngIf="!orgEdit.organizationId">A Main Branch and default date format are created with it.</p>
      </ng-container>
      <ng-template #noCreate><p class="muted small">Only Super Admin can create organizations. Pick yours on the right to edit it.</p></ng-template>
    </div>
    <div class="card flush grow">
      <table class="grid">
        <thead><tr><th>Code</th><th>Name</th><th>Branches</th><th>Users</th><th>Status</th><th></th></tr></thead>
        <tbody>
          <tr *ngFor="let o of orgs">
            <td><code>{{ o.orgCode }}</code> <span class="chip" *ngIf="o.isDefault">Default</span></td>
            <td>{{ o.orgName }}<div class="small muted">{{ o.email }}</div></td>
            <td>{{ o.branchCount }}</td><td>{{ o.userCount }}</td>
            <td><span class="chip" [class.ok]="o.isActive">{{ o.isActive ? 'Active' : 'Inactive' }}</span></td>
            <td><button class="btn xs" (click)="orgEdit = clone(o)">Edit</button></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- ============ BRANCHES ============ -->
  <div class="split" *ngIf="tab === 'branch'">
    <div class="card side">
      <div class="card-head">{{ brEdit.branchId ? 'Edit Branch' : 'New Branch' }}</div>
      <label>Code</label><input [(ngModel)]="brEdit.branchCode" [disabled]="!!brEdit.branchId" maxlength="20" placeholder="e.g. JAX">
      <label>Name</label><input [(ngModel)]="brEdit.branchName" placeholder="Branch name">
      <label>Address</label><textarea rows="2" [(ngModel)]="brEdit.address"></textarea>
      <label class="check" *ngIf="brEdit.branchId"><input type="checkbox" [(ngModel)]="brEdit.isActive"> Active</label>
      <div class="btn-row">
        <button class="btn primary" (click)="saveBranch()">{{ brEdit.branchId ? 'Save' : 'Create' }}</button>
        <button class="btn ghost" *ngIf="brEdit.branchId" (click)="brEdit = newBranch()">Cancel</button>
      </div>
    </div>
    <div class="card flush grow">
      <table class="grid">
        <thead><tr><th>Code</th><th>Name</th><th>Users</th><th>Status</th><th></th></tr></thead>
        <tbody>
          <tr *ngFor="let b of branches">
            <td><code>{{ b.branchCode }}</code></td><td>{{ b.branchName }}<div class="small muted">{{ b.address }}</div></td>
            <td>{{ b.userCount }}</td>
            <td><span class="chip" [class.ok]="b.isActive">{{ b.isActive ? 'Active' : 'Inactive' }}</span></td>
            <td><button class="btn xs" (click)="brEdit = clone(b)">Edit</button></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- ============ ROLES & PERMISSIONS ============ -->
  <div class="split" *ngIf="tab === 'role'">
    <div class="side">
      <div class="card">
        <div class="card-head">New Role</div>
        <input [(ngModel)]="newRoleName" placeholder="Role name (e.g. Counselor)" (keyup.enter)="createRole()">
        <button class="btn primary" [disabled]="!newRoleName.trim()" (click)="createRole()">Create</button>
      </div>
      <div class="role-list">
        <div class="role" *ngFor="let r of roles" [class.active]="perm?.role.roleId === r.roleId" (click)="openRole(r)">
          <span>{{ r.roleName }}</span>
          <small *ngIf="r.isSystem" class="tag-sys">System</small>
          <a *ngIf="!r.isSystem" class="x" (click)="deleteRole(r, $event)" title="Delete role">×</a>
        </div>
      </div>
    </div>
    <div class="card grow">
      <div *ngIf="!perm" class="empty">Select a role on the left to view or edit its module permissions.</div>
      <ng-container *ngIf="perm">
        <div class="card-head">{{ perm.role.roleName }} — Module Access</div>
        <p class="muted small" *ngIf="perm.locked">
          {{ perm.role.roleCode === 'SUPER_ADMIN' ? 'Super Admin always has full access.' : 'System role — only Super Admin can change it.' }}
        </p>
        <table class="grid perm">
          <thead><tr><th>Module</th><th>View</th><th>Add</th><th>Edit</th><th>Delete</th></tr></thead>
          <tbody>
            <tr *ngFor="let m of perm.modules">
              <td>{{ m.moduleName }} <small class="muted">{{ m.moduleGroup === 'ADMIN' ? 'Admin' : 'Member' }}</small></td>
              <td><input type="checkbox" [(ngModel)]="m.canView" [disabled]="perm.locked"></td>
              <td><input type="checkbox" [(ngModel)]="m.canAdd" [disabled]="perm.locked"></td>
              <td><input type="checkbox" [(ngModel)]="m.canEdit" [disabled]="perm.locked"></td>
              <td><input type="checkbox" [(ngModel)]="m.canDelete" [disabled]="perm.locked"></td>
            </tr>
          </tbody>
        </table>
        <button class="btn primary" *ngIf="!perm.locked" (click)="savePerms()">Save Permissions</button>
      </ng-container>
    </div>
  </div>`
})
export class FundamentalSettingsComponent implements OnInit {
  tab: 'org' | 'branch' | 'role' = 'org';
  orgs: any[] = [];
  orgId: number;
  orgEdit: any = this.newOrg();
  branches: any[] = [];
  brEdit: any = this.newBranch();
  roles: any[] = [];
  perm: any = null;
  newRoleName = '';

  constructor(private api: ApiService, public auth: AuthService, private toast: ToastService) {}

  ngOnInit() {
    this.orgId = this.auth.user.organizationId;
    this.loadOrgs();
  }

  clone(o: any) { return JSON.parse(JSON.stringify(o)); }
  newOrg() { return { isActive: true }; }
  newBranch() { return { isActive: true }; }
  private fail = (e: any) => this.toast.err(ApiService.message(e));

  // ---- organizations
  loadOrgs() { this.api.get<any[]>('/organizations').subscribe(o => this.orgs = o, this.fail); }
  saveOrg() {
    const o = this.orgEdit;
    const call = o.organizationId ? this.api.put('/organizations/' + o.organizationId, o) : this.api.post('/organizations', o);
    call.subscribe(r => { this.toast.ok(r.message); this.orgEdit = this.newOrg(); this.loadOrgs(); }, this.fail);
  }

  // ---- branches
  loadBranches() {
    this.brEdit = this.newBranch();
    this.api.get<any[]>('/branches', { organizationId: this.orgId }).subscribe(b => this.branches = b, this.fail);
  }
  saveBranch() {
    const b = Object.assign({ organizationId: this.orgId }, this.brEdit);
    const call = b.branchId ? this.api.put('/branches/' + b.branchId, b) : this.api.post('/branches', b);
    call.subscribe(r => { this.toast.ok(r.message); this.loadBranches(); }, this.fail);
  }

  // ---- roles
  loadRoles() {
    this.perm = null;
    this.api.get<any[]>('/roles', { organizationId: this.orgId }).subscribe(r => this.roles = r, this.fail);
  }
  createRole() {
    if (!this.newRoleName.trim()) { return; }
    this.api.post('/roles', { roleName: this.newRoleName, organizationId: this.orgId }).subscribe(r => {
      this.toast.ok(r.message); this.newRoleName = ''; this.loadRoles();
    }, this.fail);
  }
  deleteRole(r: any, ev: Event) {
    ev.stopPropagation();
    if (!confirm('Delete role "' + r.roleName + '"?')) { return; }
    this.api.delete('/roles/' + r.roleId).subscribe(x => { this.toast.ok(x.message); this.loadRoles(); }, this.fail);
  }
  openRole(r: any) { this.api.get('/roles/' + r.roleId + '/permissions').subscribe(p => this.perm = p, this.fail); }
  savePerms() {
    this.api.put('/roles/' + this.perm.role.roleId + '/permissions', this.perm.modules).subscribe(r => {
      this.toast.ok(r.message);
      this.auth.refresh().subscribe();
    }, this.fail);
  }
}
